<template>
  <div class="badge-entry" :class="{ 'badge-entry--held': isHeld && !editing }">
    <div class="text-caption text--secondary mb-1">{{ label }}</div>

    <!-- Enregistré : le numéro, ce qu'on sait du badge, qui l'a saisi ; on peut le changer ou le retirer -->
    <div v-if="isHeld && !editing" class="badge-entry__held">
      <v-icon color="success" class="mr-3">mdi-check-circle</v-icon>
      <div class="flex-grow-1">
        <div class="badge-entry__number">{{ held }}</div>
        <div class="text-caption text--secondary">
          <template v-if="verdict && !problem">{{ $t(`onboarding.badge-prep.recap-status.${verdict.status}`) }} · </template>
          {{ $t('onboarding.badge-entry.saved', { by: heldBy || '-', at: heldAtLabel }) }}
        </div>
      </div>
      <template v-if="!readonly">
        <v-btn small text @click="edit">{{ $t('onboarding.badge-entry.change') }}</v-btn>
        <v-btn small text color="error" :loading="working" @click="release">{{ $t('onboarding.badge-entry.remove') }}</v-btn>
      </template>
    </div>
    <!-- Entre la saisie et maintenant, le badge a changé de main : on le dit, la validation le redemandera -->
    <v-alert v-if="isHeld && !editing && problem" :type="alertType" dense text class="mt-2 mb-0 text-body-2">{{ message }}</v-alert>

    <!-- La saisie : vérifiée pendant la frappe, enregistrée seulement par « Valider ce badge » -->
    <template v-if="!isHeld || editing">
      <div class="d-flex align-start" style="gap: 8px">
        <v-text-field
            ref="field"
            :value="typed" :hint="$t('onboarding.badge-designation.hint')" persistent-hint
            :loading="checking" :disabled="readonly" outlined dense autocomplete="off"
            :error-messages="error ? [error] : []"
            @input="changed" @keydown.enter.prevent="hold"
        />
        <v-btn color="primary" depressed height="40" :disabled="!holdable" :loading="working" @click="hold">
          {{ $t(verdict && verdict.needsConfirmation ? 'onboarding.badge-entry.confirm-and-save' : 'onboarding.badge-entry.save') }}
        </v-btn>
        <v-btn v-if="editing" text height="40" @click="cancelEdit">{{ $t('cancel') }}</v-btn>
      </div>
      <v-alert v-if="verdict && typed.trim()" :type="alertType" dense text class="mt-1 mb-0 text-body-2">{{ message }}</v-alert>
      <div v-else-if="dirty" class="text-caption warning--text mt-1">{{ $t('onboarding.badge-entry.not-saved') }}</div>
    </template>
  </div>
</template>

<script>
import CommissioningRepository from '@repository/CommissioningRepository'
import CommissioningMixin from '../CommissioningMixin'

/**
 * Un badge pour une commande, saisi puis validé : le numéro (imprimé, ou l'UID) est cherché dans le stock pendant la
 * frappe — libre (au dépôt ou déjà remis à cette organisation), remis à une autre organisation ou hors stock (on confirme
 * : `needsConfirmation`), inutilisable, réservé par une autre commande, inconnu ou hors périmètre (refusé) — et n'est
 * enregistré sur la commande qu'au clic « Valider ce badge » (ou Entrée). Le badge part sans être activé : c'est la
 * personne qui l'activera. Enregistré, il ne se perd plus, et aucune autre commande ne peut le prendre. Une frappe non
 * validée se voit (« non enregistré ») et l'écran la signale avant qu'on parte.
 */
export default {
  name: 'BadgeEntry',

  mixins: [CommissioningMixin],

  props: {
    orderId: { type: String, required: true },
    /** professional | personal */
    kind: { type: String, required: true },
    label: { type: String, required: true },
    /** Le numéro enregistré sur la commande, ou rien. */
    held: { type: String, default: '' },
    heldBy: { type: String, default: null },
    heldAt: { type: String, default: null },
    readonly: { type: Boolean, default: false },
  },

  data() {
    return { typed: '', editing: false, verdict: null, checking: false, working: false, error: null, timer: null, asked: 0 }
  },

  computed: {
    isHeld() {
      return !!(this.held || '').trim()
    },
    /** Une frappe qui n'est pas (ou plus) celle enregistrée. */
    dirty() {
      const typed = this.typed.trim()
      return (!this.isHeld || this.editing) && typed !== '' && typed.toLowerCase() !== (this.held || '').trim().toLowerCase()
    },
    holdable() {
      return !this.readonly && !this.checking && this.typed.trim() !== '' && !!this.verdict
        && (this.verdict.status === 'free' || !!this.verdict.needsConfirmation)
    },
    /** Enregistré, mais le badge n'est plus libre comme à la saisie. */
    problem() {
      return !!this.verdict && this.verdict.status !== 'free'
    },
    alertType() {
      if (!this.verdict || this.verdict.status === 'free') return 'success'
      return this.verdict.needsConfirmation ? 'warning' : 'error'
    },
    message() {
      const v = this.verdict
      return v ? this.$t(this.messageKeyOf(v), {
        person: v.customerName || '-', organization: v.organizationName || '-', uid: v.uid || '-', printed: v.printedNumber || '-',
      }) : ''
    },
    heldAtLabel() {
      return this.heldAt ? this.$options.filters.formatDate(this.heldAt) : '-'
    },
  },

  watch: {
    dirty: {
      immediate: true,
      handler(dirty) {
        this.$emit('dirty', dirty)
      },
    },
    held: {
      immediate: true,
      handler(held) {
        this.editing = false
        this.typed = held || ''
        this.verdict = null
        // Ce qu'on sait du badge enregistré maintenant (il a pu changer de main depuis la saisie).
        if (this.isHeld) this.check(held)
      },
    },
  },

  beforeDestroy() {
    clearTimeout(this.timer)
  },

  methods: {
    /** Le message d'un verdict : un badge inutilisable dit pourquoi ; hors stock, refusé à qui ne peut pas confirmer. */
    messageKeyOf(verdict) {
      if (verdict.status === 'unusable') return `onboarding.badge-check.unusable.${verdict.state || 'out'}`
      if (verdict.status === 'out-of-stock' && !verdict.needsConfirmation) return 'onboarding.badge-check.out-of-stock-refused'
      return `onboarding.badge-check.${verdict.status}`
    },

    changed(value) {
      this.typed = value || ''
      this.error = null
      this.verdict = null
      clearTimeout(this.timer)
      if (this.typed.trim()) this.timer = setTimeout(() => this.check(this.typed), 400)
    },

    async check(identifier) {
      const asked = ++this.asked
      this.checking = true
      try {
        const verdict = await CommissioningRepository.checkBadge(this.orderId, identifier.trim())
        if (asked !== this.asked) return
        this.verdict = verdict
        this.$emit('verdict', verdict)
      } catch (e) {
        if (asked === this.asked) this.verdict = null
      } finally {
        if (asked === this.asked) this.checking = false
      }
    },

    async hold() {
      if (!this.holdable || this.working) return
      this.working = true
      this.error = null
      try {
        const result = await CommissioningRepository.holdBadge(this.orderId, this.kind, this.typed.trim(), !!this.verdict.needsConfirmation)
        this.$emit('held', result)
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.working = false
      }
    },

    async release() {
      this.working = true
      this.error = null
      try {
        const result = await CommissioningRepository.releaseBadge(this.orderId, this.kind)
        this.$emit('held', result)
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.working = false
      }
    },

    edit() {
      this.editing = true
      this.typed = ''
      this.verdict = null
      this.$nextTick(() => this.$refs.field && this.$refs.field.focus())
    },

    cancelEdit() {
      this.editing = false
      this.typed = this.held
      this.check(this.held)
    },
  },
}
</script>

<style scoped>
.badge-entry__held {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid rgba(76, 175, 80, 0.4);
  border-radius: 6px;
  background: rgba(76, 175, 80, 0.06);
}
.badge-entry__number {
  font-family: 'Roboto Mono', monospace;
  font-size: 16px;
  letter-spacing: 0.5px;
}
</style>
